import React, { useState, useEffect } from 'react';
import { X, ZoomIn, ZoomOut, Download, ChevronLeft, ChevronRight } from 'lucide-react';
import { Contract } from '../../types/contract';

interface PDFViewerProps {
  isOpen: boolean;
  onClose: () => void;
  pdfUrl?: string;
  contractId?: string;
  title?: string;
  contract?: Contract;
  renderPageFunction?: (pageNumber: number) => React.ReactNode;
  initialPage?: number; // Nova prop para a página inicial
}

export const PDFViewer: React.FC<PDFViewerProps> = ({
  isOpen,
  onClose,
  pdfUrl,
  contractId,
  title = "Visualizador de PDF",
  contract,
  renderPageFunction,
  initialPage = 1
}) => {
  const [zoom, setZoom] = useState(100);
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [totalPages] = useState(3); // Para este caso, sabemos que são 3 páginas
  const [isLoading, setIsLoading] = useState(false);

  // URL do PDF - pode ser gerada dinamicamente ou vir como prop
  const finalPdfUrl = pdfUrl || `/api/contracts/${contractId}/pdf`;

  // Atualizar a página atual quando initialPage mudar
  useEffect(() => {
    setCurrentPage(initialPage);
  }, [initialPage]);

  useEffect(() => {
    if (isOpen) {
      setIsLoading(true);
      // Simular carregamento
      const timer = setTimeout(() => setIsLoading(false), 1000);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + 25, 200));
  };

  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev - 25, 50));
  };

  const handleDownload = () => {
    if (finalPdfUrl) {
      window.open(finalPdfUrl, '_blank');
    }
  };

  const handlePrevPage = () => {
    setCurrentPage(prev => Math.max(1, prev - 1));
  };

  const handleNextPage = () => {
    setCurrentPage(prev => Math.min(totalPages, prev + 1));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50 rounded-t-xl">
          <div>
            <h2 className="text-lg font-semibold text-gray-800">{title}</h2>
            <p className="text-sm text-gray-600">Página {currentPage} de {totalPages}</p>
          </div>
          
          {/* Controles */}
          <div className="flex items-center gap-2">
            {/* Navegação de páginas */}
            <div className="flex items-center gap-1 mr-4">
              <button
                onClick={handlePrevPage}
                disabled={currentPage === 1}
                className="p-2 rounded-lg bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                title="Página anterior"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              
              <span className="px-3 py-1 bg-white border border-gray-200 rounded text-sm font-medium min-w-[60px] text-center">
                {currentPage}/{totalPages}
              </span>
              
              <button
                onClick={handleNextPage}
                disabled={currentPage === totalPages}
                className="p-2 rounded-lg bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                title="Próxima página"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>

            {/* Zoom */}
            <div className="flex items-center gap-1 mr-4">
              <button
                onClick={handleZoomOut}
                className="p-2 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors"
                title="Diminuir zoom"
              >
                <ZoomOut className="w-4 h-4" />
              </button>
              
              <span className="px-3 py-1 bg-white border border-gray-200 rounded text-sm font-medium min-w-[60px] text-center">
                {zoom}%
              </span>
              
              <button
                onClick={handleZoomIn}
                className="p-2 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors"
                title="Aumentar zoom"
              >
                <ZoomIn className="w-4 h-4" />
              </button>
            </div>

            {/* Download */}
            <button
              onClick={handleDownload}
              className="p-2 rounded-lg bg-blue-100 hover:bg-blue-200 text-blue-600 transition-colors"
              title="Baixar PDF"
            >
              <Download className="w-4 h-4" />
            </button>

            {/* Fechar */}
            <button
              onClick={onClose}
              className="p-2 rounded-lg bg-red-100 hover:bg-red-200 text-red-600 transition-colors ml-2"
              title="Fechar"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Conteúdo */}
        <div className="flex-1 overflow-auto bg-gray-100 p-4">
          {isLoading ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <p className="text-gray-600">Carregando PDF...</p>
              </div>
            </div>
          ) : (
            <div className="flex justify-center">
              <div 
                className="bg-white shadow-lg rounded-lg overflow-hidden"
                style={{ 
                  transform: `scale(${zoom / 100})`,
                  transformOrigin: 'top center',
                  transition: 'transform 0.3s ease'
                }}
              >
                {/* Conteúdo real do PDF usando a função de renderização */}
                {renderPageFunction ? (
                  <div style={{ transform: 'scale(1)', transformOrigin: 'top left' }}>
                    {renderPageFunction(currentPage)}
                  </div>
                ) : (
                  /* Fallback para quando não há função de renderização */
                  <div className="w-[210mm] min-h-[297mm] bg-white p-8">
                    <div className="text-center mb-6">
                      <h1 className="text-xl font-bold">CONTRATO DE PRESTAÇÃO DE SERVIÇOS</h1>
                      <p className="text-sm text-gray-600 mt-2">Página {currentPage} de {totalPages}</p>
                    </div>
                    
                    <div className="space-y-4 text-sm">
                      <p className="text-justify leading-relaxed">
                        Visualizador de PDF. Use os controles acima para navegar entre páginas, fazer zoom e baixar o documento.
                      </p>
                      
                      <div className="border-t border-gray-200 pt-4">
                        <p className="font-semibold">Informações:</p>
                        <p>• Zoom atual: {zoom}%</p>
                        <p>• Página: {currentPage}/{totalPages}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
